import { SALAH_METRICS, TIMELINESS, addDays, fromIso, toIso, } from '@map/shared';
/**
 * How good a prayer was, and how a run of them adds up.
 *
 * Two different questions, kept apart on purpose. A day's *score* is out of
 * five: what was done, weighted by how much each thing matters to you, all
 * multiplied by whether it was prayed on time. The building's *rating* is a
 * running average of those scores — each day moves it 40% of the way to that
 * day's score. That is what makes a habitual gap a ceiling rather than a
 * slide: never praying in congregation with everything else perfect settles
 * the rating at the score such a day earns, and it stays there. Timeliness
 * is where it bites: two days made up late take a five to under two and a
 * half, and a run of them sets it alight. A day with nothing logged is
 * neutral — it neither helps nor hurts, so a forgotten entry costs nothing.
 */
/** How far one day moves the rating toward its own score. */
export const SALAH_PULL = 0.4;
/** The rating a prayer starts from before anything has been logged. */
export const SALAH_START = 5;
/**
 * Weights from rank: the top of the list is worth n, the bottom 1. Linear on
 * purpose — the order should matter, but the last item should still count.
 */
export function salahWeights(order) {
    const n = order.length;
    return new Map(order.map((key, i) => [key, n - i]));
}
/** 0–1 for one metric: a tick as given, focus from its 1–5 scale; absent means done. */
function metricValue(key, marks) {
    const definition = SALAH_METRICS.find((m) => m.key === key);
    const raw = marks[key];
    if (definition?.input === 'scale')
        return raw === undefined ? 1 : Math.max(0, Math.min(1, (raw - 1) / 4));
    return raw === undefined ? 1 : Math.max(0, Math.min(1, raw));
}
/** One prayer on one day, out of five. */
export function scoreSalah(prayer, entry, order) {
    const factor = TIMELINESS.find((t) => t.key === entry.timeliness)?.factor ?? 0;
    if (factor === 0)
        return 0;
    const weights = salahWeights(order);
    let earned = 0;
    let possible = 0;
    for (const metric of SALAH_METRICS) {
        if (!metric.applies(prayer))
            continue;
        const weight = weights.get(metric.key) ?? 1;
        possible += weight;
        earned += weight * metricValue(metric.key, entry.marks);
    }
    const quality = possible > 0 ? earned / possible : 1;
    return round2(5 * factor * quality);
}
/** How many of the five points each metric is worth for this prayer, for showing the stakes. */
export function salahPoints(prayer, order) {
    const weights = salahWeights(order);
    const applicable = SALAH_METRICS.filter((m) => m.applies(prayer));
    const total = applicable.reduce((sum, m) => sum + (weights.get(m.key) ?? 1), 0) || 1;
    return new Map(applicable.map((m) => [m.key, round2((5 * (weights.get(m.key) ?? 1)) / total)]));
}
/**
 * Day by day from `start` to `today`. A day with nothing logged leaves the
 * rating exactly where it was.
 */
export function salahSeries(prayer, entries, order, start, today) {
    const byDay = new Map(entries.map((e) => [e.day, e]));
    const first = entries.reduce((min, e) => (e.day < min ? e.day : min), start);
    const out = [];
    let rating = SALAH_START;
    for (let day = first; day <= today; day = toIso(addDays(fromIso(day), 1))) {
        const entry = byDay.get(day);
        if (!entry) {
            out.push({ day, score: null, rating: round2(rating) });
            continue;
        }
        const score = scoreSalah(prayer, entry, order);
        rating = rating + SALAH_PULL * (score - rating);
        out.push({ day, score, rating: round2(rating) });
    }
    return out;
}
/** Weathering at which a building is alight; matches the API's BURNING. */
export const BURNING_AT = 0.8;
/**
 * The rating as neglect on the map: 4.5 and up is untouched, 0.5 and below is
 * as bad as it gets, and fire starts at about 1.3.
 */
export function salahWeathering(rating) {
    return Math.max(0, Math.min(1, (4.5 - rating) / 4));
}
function round2(n) {
    return Math.round(n * 100) / 100;
}
//# sourceMappingURL=salah.js.map